/**
 * Novanalyse — brand mark (the laboratoire glyph in the Novera visual language).
 *
 * The ecosystem's authored Novanalyse pillar: an analysis droplet with a validated tick — the
 * "validated result" idea, drawn with the NoveraMark geometry (100×100 viewBox, rounded joins).
 * Rendered either as a rounded accent tile (white mark) or a bare glyph (currentColor).
 *
 *   <NovaMark size={32} />                                   // accent tile, white mark
 *   <NovaMark size={360} tone="glyph" className="…" />       // bare mark on transparent (watermark)
 *
 * This is the single place the mark geometry lives — a later swap for an official asset is a
 * drop-in here.
 */
import type { SVGProps } from "react";

export interface NovaMarkProps extends Omit<SVGProps<SVGSVGElement>, "color"> {
  size?: number;
  /** tile = rounded accent square (white mark); glyph = bare mark on transparent. */
  tone?: "tile" | "glyph";
  /** Tile background (tone="tile"). */
  bg?: string;
  /** Mark colour (defaults per tone). */
  color?: string;
  /** Tile corner radius (tone="tile"). */
  radius?: number;
}

export function NovaMark({
  size = 32,
  tone = "tile",
  bg = "var(--nv-accent)",
  color,
  radius = 26,
  ...rest
}: NovaMarkProps) {
  const isGlyph = tone === "glyph";
  const markColor = color ?? (isGlyph ? "currentColor" : "var(--nv-accent-fg)");
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 100 100"
      role="img"
      aria-label="Novanalyse"
      {...rest}
    >
      {!isGlyph && <rect width="100" height="100" rx={radius} fill={bg} />}
      {/* analysis droplet */}
      <path
        d="M50 16 C50 16 73 43 73 61 a23 23 0 0 1-46 0 C27 43 50 16 50 16 Z"
        fill="none"
        stroke={markColor}
        strokeWidth="7"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      {/* validated tick */}
      <path
        d="M40 60 l7 7 l14-16"
        fill="none"
        stroke={markColor}
        strokeWidth="6.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export default NovaMark;
